import { apiRequest } from "@/app/lib/api-client";
import {
  API_ERRORS,
  movementListSchema,
  movementSessionListSchema,
  movementStatsSchema,
  type ApiResult,
  type MovementBucket,
  type MovementRange,
} from "@shared/contracts";
import {
  toMovementList,
  toMovementSessionPage,
  toMovementStats,
} from "./movements.converter";
import type {
  MovementListModel,
  MovementSessionPageModel,
  MovementStatsModel,
} from "./movements.model";

/**
 * THE READ PATH OF THE MOVEMENTS DOMAIN, AND IT IS THE WHOLE DOMAIN.
 *
 * 🚨 THERE IS NO WRITE HERE AND THERE NEVER WILL BE. A movement gains history in exactly one
 * way: an athlete finishes a session and the debrief sends its sets. A `create` on this service
 * would be a second way to write a set — one that could file work into a session that never
 * happened, under a movement the athlete never did, and every total on every chart is a sum
 * over those rows.
 *
 * Both methods PARSE rather than cast, which is the point of a service module: a field the
 * service renames fails here, at the boundary, and not as `undefined.toFixed()` inside a chart
 * — which on a phone is a white screen with no console anybody can read.
 */
export const movementsService = {
  /**
   * EVERYTHING THIS ATHLETE HAS EVER PERFORMED, most recently trained first.
   *
   * One request and no cursor, and `shared/contracts/movements.ts` argues it at length: the set
   * is bounded by the CATALOG rather than by how long somebody has trained, and the screen is a
   * search box that has to narrow as they type. A round trip per keystroke on a gym's wifi is
   * the difference between a search box and a search box that feels broken.
   */
  async library(): Promise<ApiResult<MovementListModel>> {
    const result = await apiRequest<unknown>("/v1/movements");
    if (!result.ok) return result;

    const parsed = movementListSchema.safeParse(result.data);
    if (!parsed.success) return { ok: false, error: API_ERRORS.SERVER_ERROR, message: "shape" };
    return { ok: true, data: toMovementList(parsed.data) };
  },

  /**
   * ONE MOVEMENT — the identity, the all-time totals, the best set and the series, together.
   *
   * 🔑 THE RANGE AND THE BUCKET ARE THE ONLY THINGS THAT REFETCH. Every metric a screen can
   * draw is already in every point, so switching between volume, top weight and reps costs
   * nothing — which is what makes those chips feel like chips. Changing the window is genuinely
   * a different set of rows.
   */
  async stats(
    movement: string,
    range: MovementRange,
    bucket: MovementBucket,
  ): Promise<ApiResult<MovementStatsModel>> {
    const query = new URLSearchParams({ range, bucket }).toString();
    const result = await apiRequest<unknown>(
      `/v1/movements/${encodeURIComponent(movement)}?${query}`,
    );
    if (!result.ok) return result;

    const parsed = movementStatsSchema.safeParse(result.data);
    if (!parsed.success) return { ok: false, error: API_ERRORS.SERVER_ERROR, message: "shape" };
    return { ok: true, data: toMovementStats(parsed.data) };
  },

  /**
   * WHAT THE ATHLETE ACTUALLY DID, DAY BY DAY — one page of sessions with their sets (BUD-387).
   *
   * 🚨 KEYSET, AND THE CURSOR IS OPAQUE. It is the service's encoding of the last row's sort
   * key, so this reads it, sends it back and never inspects it; a client that took it apart
   * would be a second definition of the list's order, and the day the sort gains a tie-break
   * the two would disagree silently rather than fail.
   *
   * ⚠️ AND IT IS A SEPARATE REQUEST FROM {@link stats}, deliberately. That one is aggregated and
   * unbounded in neither direction — points bucketed by period — while this one is scrolled: an
   * athlete two years in has hundreds of squat sessions carrying thousands of sets, and folding
   * them into the screen's first request would make the chart wait for them.
   */
  async sessions(
    movement: string,
    cursor: string | null,
  ): Promise<ApiResult<MovementSessionPageModel>> {
    /* `URLSearchParams` rather than string concatenation, because the cursor is base64-ish and
       a `+` in a query string is a SPACE. Building this by hand is how a page silently restarts
       at page one — which the service now refuses outright rather than swallowing (BUD-303). */
    const query = cursor === null ? "" : `?${new URLSearchParams({ cursor }).toString()}`;
    const result = await apiRequest<unknown>(
      `/v1/movements/${encodeURIComponent(movement)}/sessions${query}`,
    );
    if (!result.ok) return result;

    const parsed = movementSessionListSchema.safeParse(result.data);
    if (!parsed.success) return { ok: false, error: API_ERRORS.SERVER_ERROR, message: "shape" };
    return { ok: true, data: toMovementSessionPage(parsed.data) };
  },
};
